import { apyToAprPercent } from '../../../utils/parsing'
import type { YieldFetcher } from '../../types'
import { fetchDefiLlamaApy } from './defillama'

/**
 * Re Protocol — `reUSD` (senior) and `reUSDe` (junior) supply APR.
 *
 * Re is a **reinsurance** protocol: deposits underwrite insurance risk,
 * and the blended rate is SOFR + 250 bps on the off-chain leg plus the
 * 7-day trailing sUSDe basis rate + 250 bps on the on-chain leg. None of
 * that is derivable on-chain — the NAV oracle publishes an accrued
 * price once a day with **no round history** (`roundId` is always 0), so
 * a rate-delta would need archival calls on four chains, which is the
 * exact fragility the Spark spUSDG fetcher was just rewritten to shed.
 *
 * DefiLlama indexes both tokens under `project=re` and its TVL for the
 * reUSD pool matches the Ethereum supply × NAV to the dollar
 * ($169,447,829), which is a strong signal it is tracking the same
 * thing we are. Same DefiLlama-only posture as
 * [nativeCreditPool.ts](./nativeCreditPool.ts): a `0` here means
 * "unknown", not "no yield" — the `exchangeRate` still shows accrual to
 * date.
 *
 * Ethereum-scoped by DefiLlama, but the NAV oracle publishes the **same
 * price on all four chains** (verified: 1.0922553299880637 simultaneously
 * on Ethereum, Base, Arbitrum and Avalanche), so one rate legitimately
 * covers every deployment.
 *
 * Docs: https://docs.re.xyz/
 */

/** Registry `yieldKey`s. */
export const REUSD_KEY = 'RE::reUSD'
export const REUSDE_KEY = 'RE::reUSDe'

/**
 * Canonical 1delta token-list `assetGroup`s, emitted alongside the
 * registry keys so the lending join (`intrinsicYields[assetGroup]`)
 * resolves — same dual-key convention as [frax.ts](./frax.ts).
 *
 * **Three unrelated tokens carry the `REUSD` ticker** in the list —
 * Re Protocol's, Resupply's (`Resupply USD::REUSD`) and RealToken's
 * (`Realtoken Ecosystem USD::REUSD`). Only the fully-qualified group is
 * safe to key on; a bare `REUSD` would cross-contaminate all three.
 */
export const REUSD_GROUP_KEY = 'Re Protocol reUSD::REUSD'
export const REUSDE_GROUP_KEY = 'Re Protocol reUSDe::REUSDE'

/** DefiLlama pool UUIDs (`project=re`). APYs are the values at pinning
 *  time (2026-08-01) — sanity anchors, not assertions. */
const REUSD_POOL = '1c312830-ee96-40c9-b55f-b0f209ca6ebd' // 6.26 %
const REUSDE_POOL = '145810df-dc01-43e7-8033-e0aa5dceb767' // 12.25 %

export const reProtocolFetcher: YieldFetcher = {
  label: 'RE_PROTOCOL',
  fetch: async () => {
    // Independent per token — reUSDe is a tenth of reUSD's size and must
    // not be able to take the senior tranche's rate down with it.
    const [reusd, reusde] = await Promise.allSettled([
      fetchDefiLlamaApy(REUSD_POOL),
      fetchDefiLlamaApy(REUSDE_POOL),
    ])

    const out: Record<string, number> = {}
    if (reusd.status === 'fulfilled') {
      const apr = apyToAprPercent(reusd.value)
      out[REUSD_KEY] = apr
      out[REUSD_GROUP_KEY] = apr
    }
    if (reusde.status === 'fulfilled') {
      const apr = apyToAprPercent(reusde.value)
      out[REUSDE_KEY] = apr
      out[REUSDE_GROUP_KEY] = apr
    }
    if (Object.keys(out).length === 0) {
      throw new Error('Re Protocol: DefiLlama returned no apy for either pool')
    }
    return out
  },
}
